import React from 'react';
import { styled } from '@mui/material/styles';
import { keyframes } from 'tss-react';
import { themeColors } from '../../BaseComponents/ThemeProvider/AvenyThemeColors';
import EDTooltip from '../../BaseComponents/Tooltip/EDTooltip';
import { useIsTouchDevice } from '../BreakpointContext/BreakpointContextUtils';
import { useRegisterFloatingMenu } from './FloatingMenuPresenceContext';
import { useFloatingBottomAreaSysoption } from './FloatingBottomAreaSysoptionContext';

/**
 * Height of the floating menu pill in pixels.
 */
export const FLOATING_MENU_HEIGHT = 56;
const FLOATING_MENU_BORDER_WIDTH = 1;
const FLOATING_MENU_PADDING_BOTTOM = 16;
export const FLOATING_MENU_MAX_WIDTH_PX = 350;
export const FLOATING_MENU_SIDE_PADDING_PX = 12;
export const FLOATING_MENU_FAB_SIZE_PX = 48;
export const FLOATING_MENU_CLUSTER_GAP_PX = 12;
export const FLOATING_MENU_CLUSTER_RESERVED_WIDTH_PX =
  FLOATING_MENU_FAB_SIZE_PX + FLOATING_MENU_CLUSTER_GAP_PX;
export const FLOATING_MENU_CLUSTER_MAX_WIDTH_PX =
  FLOATING_MENU_MAX_WIDTH_PX + FLOATING_MENU_CLUSTER_RESERVED_WIDTH_PX;
const FLOATING_MENU_SAFE_AREA_BOTTOM = 'env(safe-area-inset-bottom, 0px)';
const FLOATING_MENU_SAFE_AREA_LEFT = 'env(safe-area-inset-left, 0px)';
const FLOATING_MENU_SAFE_AREA_RIGHT = 'env(safe-area-inset-right, 0px)';
export const FLOATING_MENU_SAFE_AREA_PADDING_BOTTOM = `max(${FLOATING_MENU_PADDING_BOTTOM}px, ${FLOATING_MENU_SAFE_AREA_BOTTOM})`;
export const FLOATING_MENU_SAFE_AREA_PADDING_LEFT = `max(${FLOATING_MENU_SIDE_PADDING_PX}px, ${FLOATING_MENU_SAFE_AREA_LEFT})`;
export const FLOATING_MENU_SAFE_AREA_PADDING_RIGHT = `max(${FLOATING_MENU_SIDE_PADDING_PX}px, ${FLOATING_MENU_SAFE_AREA_RIGHT})`;
// px, should match borderRadius of FloatingMenuContainer

/** Numeric value (px) for use where a number is needed instead of a CSS string. */
export const FLOATING_MENU_CLEARANCE_PX =
  FLOATING_MENU_HEIGHT +
  FLOATING_MENU_BORDER_WIDTH * 2 +
  FLOATING_MENU_PADDING_BOTTOM;

/** Space (in px) needed to clear the floating menu when it is visible. */
export const FLOATING_MENU_CLEARANCE = `calc(${
  FLOATING_MENU_HEIGHT + FLOATING_MENU_BORDER_WIDTH * 2
}px + max(${FLOATING_MENU_PADDING_BOTTOM}px, ${FLOATING_MENU_SAFE_AREA_BOTTOM}))`;

/**
 * Returns true when the floating bottom menu is rendered on this viewport.
 */
export const useIsFloatingMenuVisible = (): boolean => {
  const isTouchDevice = useIsTouchDevice();
  const floatingBottomAreaSysoptionContext = useFloatingBottomAreaSysoption();
  return Boolean(
    isTouchDevice &&
      floatingBottomAreaSysoptionContext.floatingBottomAreaEnabled
  );
};

const readFloatingMenuSafeAreaInsetBottomPx = (): number => {
  if (typeof document === 'undefined') {
    return 0;
  }

  const measurementElement = document.createElement('div');
  measurementElement.style.position = 'fixed';
  measurementElement.style.bottom = '0';
  measurementElement.style.height = FLOATING_MENU_SAFE_AREA_BOTTOM;
  measurementElement.style.visibility = 'hidden';
  measurementElement.style.pointerEvents = 'none';
  measurementElement.style.padding = '0';
  measurementElement.style.border = '0';

  document.body.appendChild(measurementElement);
  const safeAreaInsetBottomPx =
    Number.parseFloat(getComputedStyle(measurementElement).height) || 0;
  document.body.removeChild(measurementElement);

  return safeAreaInsetBottomPx;
};

/**
 * Total vertical space the floating menu occupies (pill height + bottom padding),
 * in pixels. Use this to add `paddingBottom` to scroll containers so content
 * isn't hidden behind the menu.
 */
export const useFloatingMenuReservedHeight = (): number => {
  const visible = useIsFloatingMenuVisible();
  const [safeAreaInsetBottomPx, setSafeAreaInsetBottomPx] = React.useState(0);

  React.useEffect(() => {
    if (!visible || typeof window === 'undefined') {
      setSafeAreaInsetBottomPx(0);
      return;
    }

    const updateSafeAreaInsetBottom = () => {
      setSafeAreaInsetBottomPx(readFloatingMenuSafeAreaInsetBottomPx());
    };

    updateSafeAreaInsetBottom();

    const visualViewport = window.visualViewport;
    window.addEventListener('resize', updateSafeAreaInsetBottom);
    visualViewport?.addEventListener('resize', updateSafeAreaInsetBottom);

    return () => {
      window.removeEventListener('resize', updateSafeAreaInsetBottom);
      visualViewport?.removeEventListener('resize', updateSafeAreaInsetBottom);
    };
  }, [visible]);

  return visible
    ? FLOATING_MENU_HEIGHT +
        FLOATING_MENU_BORDER_WIDTH * 2 +
        Math.max(FLOATING_MENU_PADDING_BOTTOM, safeAreaInsetBottomPx)
    : 0;
};

/**
 * Fixed-position wrapper that places the menu at the bottom of the viewport,
 * below the EDNavigationOverlay (fab - 1) so the detail overlay covers it.
 */
const FloatingMenuRoot = styled('div', { name: 'FloatingMenu', slot: 'Root' })(
  ({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    boxSizing: 'border-box',
    paddingBottom: FLOATING_MENU_SAFE_AREA_PADDING_BOTTOM,
    paddingLeft: FLOATING_MENU_SAFE_AREA_PADDING_LEFT,
    paddingRight: FLOATING_MENU_SAFE_AREA_PADDING_RIGHT,
    pointerEvents: 'none',
    zIndex: theme.zIndex.fab - 2,
  })
);

const FloatingMenuCluster = styled('div', {
  name: 'FloatingMenu',
  slot: 'Cluster',
})(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  width: '100%',
  maxWidth: `${FLOATING_MENU_CLUSTER_MAX_WIDTH_PX}px`,
}));

const expandFromRight = keyframes`
  from {
    clip-path: inset(-40px -40px -40px calc(100% + 40px));
    opacity: 0;
  }
  to {
    clip-path: inset(-40px);
    opacity: 1;
  }
`;

/**
 * Glass pill container with backdrop blur and semi-transparent background.
 */
const FloatingMenuContainer = styled('div', {
  name: 'FloatingMenu',
  slot: 'Container',
})(() => ({
  boxSizing: 'border-box',
  borderRadius: '100px',
  pointerEvents: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '0 4px',
  width: `calc(100% - ${FLOATING_MENU_CLUSTER_RESERVED_WIDTH_PX}px)`,
  height: '56px',
  backdropFilter: 'blur(8px) saturate(180%)',
  border: `${FLOATING_MENU_BORDER_WIDTH}px solid ${themeColors.customGlass.buttonBorder}`,
  boxShadow: `0px 8px 32px ${themeColors.customGlass.buttonShadow}, inset 0px 0.5px 0px rgba(255,255,255,0.6)`,
  backgroundColor: themeColors.customGlass.buttonBackground,
  color: themeColors.customGlass.buttonColor,
  animation: `${expandFromRight} 300ms ease forwards`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

interface IFloatingMenuSlotProps {
  span?: number;
}

const FloatingMenuSlotRoot = styled('div', {
  name: 'FloatingMenu',
  slot: 'Slot',
  shouldForwardProp: prop => prop !== 'span',
})<IFloatingMenuSlotProps>(({ span = 1 }) => ({
  flex: `${span} 1 0`,
  minWidth: 0,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const FloatingMenuSlot: React.FC<
  React.PropsWithChildren<IFloatingMenuSlotProps>
> = ({ children, span = 1 }) => (
  <FloatingMenuSlotRoot span={span}>{children}</FloatingMenuSlotRoot>
);

/**
 * Button element styled for the floating menu — icon + label stacked vertically.
 */
const FloatingMenuButtonRoot = styled('button', {
  name: 'FloatingMenu',
  slot: 'Button',
  shouldForwardProp: prop => prop !== 'primary',
})<{ primary?: boolean }>(({ theme, primary }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  gap: '1px',
  border: 0,
  padding: '1px',
  cursor: 'pointer',
  transition: 'all 200ms',
  borderRadius: '14px',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  color: primary
    ? theme.palette.primary.main
    : themeColors.customGlass.buttonText,
  '&:active': {
    transform: 'scale(0.95)',
  },
  '&:disabled': {
    opacity: 0.4,
    cursor: 'default',
    color: themeColors.customGlass.buttonText,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:active': {
      transform: 'none',
    },
  },
}));

interface IFloatingMenuButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  primary?: boolean;
  /** Text shown in a tooltip when hovering/focusing the button. */
  title?: React.ReactNode;
}

/**
 * Button element styled for the floating menu — icon + label stacked vertically.
 * Optionally wrapped in a tooltip when `tooltip` is provided.
 */
export const FloatingMenuButton = React.forwardRef<
  HTMLButtonElement,
  IFloatingMenuButtonProps
>(({ title, ...buttonProps }, ref) => {
  const button = <FloatingMenuButtonRoot ref={ref} {...buttonProps} />;

  if (title) {
    return (
      <EDTooltip enableClickOnTouch={buttonProps.disabled} title={title}>
        {button}
      </EDTooltip>
    );
  }

  return button;
});

/**
 * Caption label for a FloatingMenuButton.
 */
export const FloatingMenuButtonLabel = styled('span', {
  name: 'FloatingMenu',
  slot: 'ButtonLabel',
})(() => ({
  maxWidth: '100%',
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: 'normal',
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: themeColors.customGlass.buttonText,
}));

/**
 * Floating glass pill menu fixed at the bottom of the viewport.
 * Positioned below the EDNavigationOverlay so the detail overlay covers it.
 */
const FloatingMenu: React.FC<React.PropsWithChildren> = ({ children }) => {
  useRegisterFloatingMenu();

  return (
    <FloatingMenuRoot>
      <FloatingMenuCluster>
        <FloatingMenuContainer>
          {React.Children.toArray(children)
            .filter(React.isValidElement)
            .map((child, index) => {
              if (child.type === FloatingMenuSlot) {
                return child;
              }

              return (
                <FloatingMenuSlot key={child.key ?? index}>
                  {child}
                </FloatingMenuSlot>
              );
            })}
        </FloatingMenuContainer>
      </FloatingMenuCluster>
    </FloatingMenuRoot>
  );
};

export default FloatingMenu;
